import { useEffect, useState } from 'react';
import { useApp } from './app';
import { recentDrops, type RecentPlace } from './recents';
import type { Ride } from './types';

/** The rider's last trips (GET /v1/rider/rides), shared by Home and Search and kept for a minute. */
let cache: { at: number; rides: Ride[] } | null = null;
let inflight: Promise<Ride[]> | null = null;

export function useRecentRides(): Ride[] | null {
  const { api, signedIn } = useApp();
  const [rides, setRides] = useState<Ride[] | null>(cache?.rides ?? null);
  useEffect(() => {
    if (!signedIn) return;
    if (cache && Date.now() - cache.at < 60_000) return setRides(cache.rides);
    let live = true;
    inflight ??= api
      .rides(1, 20)
      .then((p) => {
        cache = { at: Date.now(), rides: p.items };
        return p.items;
      })
      .finally(() => (inflight = null));
    inflight.then((r) => live && setRides(r), () => live && setRides((x) => x ?? []));
    return () => {
      live = false;
    };
  }, [api, signedIn]);
  return rides;
}

/** Recent drop-offs (not saved places, not the pickup), newest first. Null while loading. */
export function useRecentPlaces(limit = 5): RecentPlace[] | null {
  const { rider, draft } = useApp();
  const rides = useRecentRides();
  return rides ? recentDrops(rides, { saved: rider?.places, pickup: draft.pickup, limit }) : null;
}

/** For sign-out and tests. */
export const clearRecents = () => {
  cache = null;
};
